import apiClient from '../lib/api-client';
import { getAttemptToken } from '../lib/attempt-token';

function attemptHeaders(attemptId: string): {
  headers?: Record<string, string>;
} {
  const token = getAttemptToken(attemptId);
  return token ? { headers: { 'x-attempt-token': token } } : {};
}

export interface ResultsWithReview {
  attemptId: string;
  exam: { _id: string; title: string; type: string; level?: string; duration?: number; passingScore?: number };
  user?: { _id: string; name?: string; email?: string };
  score: number | null;
  percentage: number | null;
  passed: boolean | null;
  gradingStatus?: 'not-required' | 'pending' | 'processing' | 'completed' | 'failed';
  gradingError?: string;
  gradingStartedAt?: string;
  gradingCompletedAt?: string;
  speakingGradingResult?: {
    model: string;
    sttModel: string;
    gradedAt: string;
    overallScore: number;
    overallFeedback: string;
    gradedAnswers: number;
    failedAnswers: number;
  };
  startTime: string;
  endTime: string;
  duration: number;
  totalAnswers: number;
  totalQuestions: number;
  answers: Array<{
    questionId: string;
    answer: unknown;
    isCorrect?: boolean;
    points?: number;
    timeSpent?: number;
    transcript?: string;
    aiScore?: number;
    aiFeedback?: string;
    gradingState?: 'pending' | 'processing' | 'completed' | 'failed';
    criteriaScores?: Array<{ criterion: string; met: boolean; comment: string }>;
    questionText?: string;
    context?: string;
    questionType?: string;
    theme?: string;
    maxPoints?: number;
    options?: string[];
    correctAnswer?: unknown;
    explanation?: string;
    media?: { audio?: string; image?: string; video?: string };
  }>;
}

export interface ScoreHistoryEntry {
  attemptId: string;
  exam: { _id: string; title: string; type: string; level?: string };
  score: number;
  percentage: number;
  passed: boolean;
  answeredQuestions: number;
  date: string;
  duration: number;
}

export interface PerformanceSummary {
  totalAttempts: number;
  averageScore: number;
  passRate: number;
  totalStudyTime: number;
  passedExams: number;
  failedExams: number;
}

export const resultsService = {
  async getResults(attemptId: string) {
    const response = await apiClient.get<ResultsWithReview>(
      `/results/${attemptId}`,
      attemptHeaders(attemptId),
    );
    return response.data;
  },

  async getResultsWithReview(attemptId: string) {
    const response = await apiClient.get<ResultsWithReview>(
      `/results/${attemptId}/review`,
      attemptHeaders(attemptId),
    );
    return response.data;
  },

  async getScoreHistory(examId?: string) {
    const params = examId ? { examId } : {};
    const response = await apiClient.get<ScoreHistoryEntry[]>('/results/history', { params });
    return response.data;
  },

  async getPerformanceSummary() {
    const response = await apiClient.get<PerformanceSummary>('/results/summary');
    return response.data;
  },
};
